/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState, useCallback } from "react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ClickToCallButton from "@/components/ClickToCallButton";
import { toast } from "sonner";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";
import {
  MessageCircle, Send, ExternalLink, LogIn, LogOut, Volume2, VolumeX, Loader2,
  Hand, AlertCircle, CheckCircle2, Eye, X, RefreshCw,
} from "lucide-react";

/**
 * Full staff live-chat console.
 *
 * Flow:
 *  1. Staff lands on the "Live Chat" tab → if `chat_signed_on=false` they
 *     see a sign-on card (re-enter password). On success backend sets
 *     `chat_signed_on=true`.
 *  2. Once signed on the panel splits into:
 *      - Left: sidebar with FIFO unassigned queue + your active chats + (admin
 *        only) all assigned chats for shadow-monitoring.
 *      - Right: the active conversation thread + composer.
 *  3. A pop-out button opens `/staff-chat` in a new browser tab so staff can
 *     keep responding to patients while navigating the rest of the portal.
 *
 * Heartbeat: while signed on, this component polls `/api/chat/staff/status`
 * every 60s. The server refreshes `chat_active_at` on each call so we don't
 * appear stale to patients.
 */

const POLL_MS = 4000;          // conversation list refresh cadence
const HEARTBEAT_MS = 60000;    // sign-on heartbeat
const NEW_MSG_SOUND_URL = "data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA="; // tiny silent placeholder; real ping below

function ping() {
  // Tiny synthesized 880 Hz blip — no network asset needed.
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = "sine"; osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.start(); osc.stop(ctx.currentTime + 0.35);
  } catch { /* user hasn't interacted yet — browsers block AudioContext */ }
}

export default function StaffChatPanel({ embedded = false }) {
  const { user } = useAuth();
  const [signedOn, setSignedOn] = useState(false);
  const [signingOn, setSigningOn] = useState(false);
  const [signOffLoading, setSignOffLoading] = useState(false);
  const [pwd, setPwd] = useState("");
  const [signOnError, setSignOnError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [muted, setMuted] = useState(false);
  const [agents, setAgents] = useState([]);
  const [canned, setCanned] = useState([]);
  const [cannedOpen, setCannedOpen] = useState(false);
  const [cannedIdx, setCannedIdx] = useState(0);
  const [pendingCannedId, setPendingCannedId] = useState(null);

  const lastSeenIdsRef = useRef(new Set()); // for new-message ping detection
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ------- Sign-on/off -------
  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await API.get("/chat/staff/status");
      setSignedOn(!!data.signed_on);
      setIsAdmin(!!data.is_admin);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { refreshStatus(); }, []);

  // Heartbeat while signed on so we don't go stale on the patient side.
  useEffect(() => {
    if (!signedOn) return;
    const id = setInterval(() => { refreshStatus(); }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [signedOn]);

  const handleSignOn = async (e) => {
    e?.preventDefault();
    if (!pwd) return;
    setSigningOn(true);
    setSignOnError("");
    try {
      await API.post("/chat/staff/sign-on", { password: pwd });
      setPwd("");
      setSignedOn(true);
      toast.success("You're now live — patients can reach you.");
    } catch (err) {
      setSignOnError(err?.response?.data?.detail || "Couldn't sign on. Please try again.");
    } finally {
      setSigningOn(false);
    }
  };

  const handleSignOff = async () => {
    setSignOffLoading(true);
    try {
      await API.post("/chat/staff/sign-off");
      setSignedOn(false);
      setActiveConvId(null);
      toast("You're offline. Patients now see the email-fallback message.", { icon: "👋" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign off — try again.");
    } finally {
      setSignOffLoading(false);
    }
  };

  // ------- Conversations + messages -------
  const loadConvs = useCallback(async () => {
    try {
      const { data } = await API.get("/chat/conversations?status=open");
      setConversations(Array.isArray(data) ? data : []);
    } catch { /* surface only on first load */ }
  }, []);

  const loadAgents = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await API.get("/chat/staff/online-agents");
      setAgents(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, [isAdmin]);

  // Load canned responses once on sign-on; refresh every few minutes so admin
  // edits propagate without a page reload.
  const loadCanned = useCallback(async () => {
    try {
      const { data } = await API.get("/chat/canned-responses");
      setCanned(Array.isArray(data) ? data : []);
    } catch { /* silent — composer just shows no suggestions */ }
  }, []);

  useEffect(() => {
    if (!signedOn) return;
    loadCanned();
    const id = setInterval(loadCanned, 180000);
    return () => clearInterval(id);
  }, [signedOn]);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const { data } = await API.get(`/chat/messages/${convId}`);
      // Endpoint now returns `{messages, status, rating, ...}`. Stay
      // backwards compatible with any old shape (cached client / preview env)
      // by accepting either a bare array or the new envelope.
      const arr = Array.isArray(data) ? data : (data?.messages ?? []);
      // Detect new messages from the patient → ping if not muted.
      const previousIds = lastSeenIdsRef.current;
      const newPatientMsgs = arr.filter(m => m.sender_role === "patient" && !previousIds.has(m.id));
      lastSeenIdsRef.current = new Set(arr.map(m => m.id));
      if (newPatientMsgs.length > 0 && !muted && previousIds.size > 0) {
        ping();
      }
      setMessages(arr);
    } catch { /* keep old */ }
  }, [muted]);

  useEffect(() => {
    if (!signedOn) return;
    loadConvs();
    loadAgents();
    const id = setInterval(() => { loadConvs(); loadAgents(); }, POLL_MS);
    return () => clearInterval(id);
  }, [signedOn, isAdmin]);

  useEffect(() => {
    if (!activeConvId) { setMessages([]); lastSeenIdsRef.current = new Set(); return; }
    loadMessages(activeConvId);
    const id = setInterval(() => loadMessages(activeConvId), POLL_MS);
    return () => clearInterval(id);
  }, [activeConvId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Tab title unread count (cumulative across queue + your assigned chats)
  useEffect(() => {
    if (!signedOn) return;
    const unread = conversations.reduce((sum, c) => sum + (c.unread_staff || 0), 0);
    const baseTitle = "GLPFlo Live Chat";
    document.title = unread > 0 ? `(${unread}) ${baseTitle}` : baseTitle;
    return () => { document.title = baseTitle; };
  }, [conversations, signedOn]);

  // ------- Actions -------
  const handleClaim = async (convId) => {
    try {
      await API.put(`/chat/conversations/${convId}/claim`);
      setActiveConvId(convId);
      loadConvs();
      toast.success("Chat claimed.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't claim that chat.";
      toast.error(msg);
      loadConvs();
    }
  };

  // Filter canned suggestions based on what staff has typed after the slash.
  // Triggered when input starts with "/" and contains no whitespace yet.
  const cannedQuery = (() => {
    if (!input.startsWith("/")) return null;
    const rest = input.slice(1);
    if (rest.includes(" ")) return null; // whitespace closes the picker
    return rest.toLowerCase();
  })();
  const cannedFiltered = (() => {
    if (cannedQuery === null) return [];
    if (!cannedQuery) return canned.slice(0, 8);
    return canned.filter(c =>
      c.slash.toLowerCase().startsWith(cannedQuery) ||
      c.title.toLowerCase().includes(cannedQuery) ||
      c.category.toLowerCase().includes(cannedQuery),
    ).slice(0, 8);
  })();

  // Auto-open / close picker as input changes; keep highlight in range.
  useEffect(() => {
    if (cannedQuery !== null && cannedFiltered.length > 0 && canTypeInActive) {
      setCannedOpen(true);
      setCannedIdx(idx => Math.min(idx, cannedFiltered.length - 1));
    } else {
      setCannedOpen(false);
    }
  }, [input, canned.length]);

  const expandCanned = (canned_row) => {
    if (!canned_row) return;
    let body = canned_row.body || "";
    // Replace {{patient_name}} with the active conversation's patient name.
    const patientName = (active && (active.patient_name || "").trim()) || "there";
    body = body.replaceAll("{{patient_name}}", patientName);
    setInput(body);
    setPendingCannedId(canned_row.id);
    setCannedOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleInputKeyDown = (e) => {
    if (!cannedOpen) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCannedIdx(i => Math.min(i + 1, cannedFiltered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCannedIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      expandCanned(cannedFiltered[cannedIdx]);
    } else if (e.key === "Escape") {
      e.preventDefault(); setCannedOpen(false);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim() || !activeConvId) return;
    setSending(true);
    try {
      await API.post("/chat/messages", { conversation_id: activeConvId, message: input.trim() });
      // Best-effort — bump the canned counter only if the staff actually sent
      // a message that originated from a canned template (and didn't clear it).
      if (pendingCannedId) {
        API.post(`/chat/canned-responses/${encodeURIComponent(pendingCannedId)}/used`).catch(() => {});
      }
      setInput("");
      setPendingCannedId(null);
      loadMessages(activeConvId);
      loadConvs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — try again.");
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!activeConvId) return;
    if (!(await brandedConfirm({ title: 'Are you sure?', description: "End this chat? The patient will see it as closed.", confirmText: 'Confirm', cancelText: 'Cancel', tone: 'info' }))) return;
    try {
      await API.put(`/chat/conversations/${activeConvId}/close`);
      toast.success("Chat closed.");
      setActiveConvId(null);
      loadConvs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't close.");
    }
  };

  const handleReassign = async (convId, staffId) => {
    try {
      await API.put(`/chat/conversations/${convId}/reassign`, { staff_id: staffId });
      toast.success(staffId ? "Reassigned." : "Returned to queue.");
      loadConvs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reassign.");
    }
  };

  const popOut = () => {
    window.open("/staff-chat", "GLPFlo Live Chat", "width=1100,height=780");
  };

  // ------- Derived -------
  const myUserId = user?.id || user?._id;
  const queue = conversations.filter(c => !c.assigned_to).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  const mine = conversations.filter(c => c.assigned_to === myUserId);
  const others = isAdmin ? conversations.filter(c => c.assigned_to && c.assigned_to !== myUserId) : [];
  const active = conversations.find(c => c.id === activeConvId) || null;
  const canTypeInActive = !!active && active.assigned_to === myUserId && active.status !== "closed";

  // ============== Render ==============
  if (!signedOn) {
    return (
      <div className={`${embedded ? "" : "min-h-screen flex items-center justify-center bg-slate-50 px-4"}`}>
        <div className="max-w-md w-full bg-white rounded-2xl border border-purple-100 shadow-[0_10px_40px_-15px_rgba(124,58,237,0.25)] p-7" data-testid="chat-signon-card">
          <div className="w-12 h-12 rounded-2xl bg-purple-600 text-white flex items-center justify-center mb-4">
            <MessageCircle className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Sign on for live chat</h2>
          <p className="text-sm text-slate-600 mb-5 leading-relaxed">
            Re-enter your password to go live. While signed on, patients see you as available and can start a conversation. You&apos;ll stay live until you sign off — even after closing this tab.
          </p>
          <form onSubmit={handleSignOn} className="space-y-3">
            <Input
              type="password"
              placeholder="Your portal password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              autoFocus
              data-testid="chat-signon-password"
              className="rounded-full"
            />
            {signOnError && (
              <p className="text-xs text-rose-600 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> {signOnError}</p>
            )}
            <Button type="submit" disabled={signingOn || !pwd} className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="chat-signon-submit">
              {signingOn ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
              Sign on
            </Button>
          </form>
          <p className="text-[11px] text-slate-400 text-center mt-4">This is separate from your portal login. Required even when already signed in.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? "" : "h-screen"} flex flex-col bg-slate-50`} data-testid="staff-chat-console">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-100 px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex w-2 h-2 rounded-full bg-emerald-500 ring-4 ring-emerald-100 animate-pulse" />
          <span className="text-sm font-semibold text-slate-900">You&apos;re live</span>
          {isAdmin && <span className="text-[10px] uppercase tracking-wider bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 font-bold">Admin</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setMuted(m => !m)}
            className="text-slate-400 hover:text-slate-700 p-2 rounded-full hover:bg-slate-100 transition-colors"
            title={muted ? "Unmute new-message ping" : "Mute new-message ping"}
            data-testid="chat-mute-btn"
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          {!embedded ? null : (
            <Button variant="outline" size="sm" className="rounded-full" onClick={popOut} data-testid="chat-popout-btn">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Pop out
            </Button>
          )}
          <Button variant="outline" size="sm" className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50" onClick={handleSignOff} disabled={signOffLoading} data-testid="chat-signoff-btn">
            {signOffLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5 mr-1.5" />}
            Sign off
          </Button>
        </div>
      </header>

      {/* Main split */}
      <div className="flex-1 grid sm:grid-cols-[300px_1fr] min-h-0">
        {/* Sidebar */}
        <aside className="bg-white border-r border-slate-100 overflow-y-auto" data-testid="chat-sidebar">
          {/* Queue */}
          <Section title={`Unassigned queue · FIFO (${queue.length})`} testId="chat-queue">
            {queue.length === 0 ? <Empty msg="Queue is empty." /> : queue.map((c) => (
              <ConvRow key={c.id} c={c} variant="queue" onClick={() => handleClaim(c.id)} />
            ))}
          </Section>
          {/* Mine */}
          <Section title={`Your active chats (${mine.length})`} testId="chat-mine">
            {mine.length === 0 ? <Empty msg="No active chats yet — claim one from the queue." /> : mine.map((c) => (
              <ConvRow key={c.id} c={c} active={c.id === activeConvId} onClick={() => setActiveConvId(c.id)} />
            ))}
          </Section>
          {/* Admin shadow */}
          {isAdmin && (
            <Section title={`All other agents (${others.length})`} testId="chat-others">
              {others.length === 0 ? <Empty msg="No other chats in progress." /> : others.map((c) => (
                <ConvRow key={c.id} c={c} variant="watch" active={c.id === activeConvId} onClick={() => setActiveConvId(c.id)} />
              ))}
            </Section>
          )}
        </aside>

        {/* Right pane */}
        <section className="flex flex-col min-h-0" data-testid="chat-thread">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
              Select a conversation on the left.
            </div>
          ) : (
            <>
              <div className="bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{active.patient_name || "Patient"}</p>
                  <p className="text-xs text-slate-400 truncate">{active.patient_email}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {active.patient_id && (
                    <ClickToCallButton
                      patientId={active.patient_id}
                      patientLabel={active.patient_name || "patient"}
                      chatThreadId={active.id}
                      compact
                      className="border-slate-200"
                    />
                  )}
                  {active.assigned_to ? (
                    <span className="text-[11px] inline-flex items-center gap-1 bg-purple-50 text-purple-700 rounded-full px-2.5 py-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Agent: {active.assigned_name}
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleClaim(active.id)} data-testid="chat-claim-btn">
                      <Hand className="w-3.5 h-3.5 mr-1.5" /> Claim
                    </Button>
                  )}
                  {isAdmin && active.assigned_to && (
                    <select
                      value={active.assigned_to || ""}
                      onChange={(e) => handleReassign(active.id, e.target.value || null)}
                      className="text-xs rounded-full border border-slate-200 bg-white px-3 py-1 focus:outline-none focus:ring-2 focus:ring-purple-200"
                      data-testid="chat-reassign-select"
                    >
                      {agents.find(a => a.id === active.assigned_to) ? null : (
                        <option value={active.assigned_to}>{active.assigned_name} (offline)</option>
                      )}
                      {agents.map(a => <option key={a.id} value={a.id}>{a.name} ({a.role})</option>)}
                      <option value="">↩ Return to queue</option>
                    </select>
                  )}
                  {((active.assigned_to === myUserId) || isAdmin) && (
                    <Button size="sm" variant="ghost" className="rounded-full text-rose-700 hover:bg-rose-50" onClick={handleClose} data-testid="chat-close-btn">
                      <X className="w-3.5 h-3.5 mr-1.5" /> End chat
                    </Button>
                  )}
                </div>
              </div>

              {/* Read-only banner for admins shadowing */}
              {!canTypeInActive && active.assigned_to && active.assigned_to !== myUserId && isAdmin && (
                <div className="bg-amber-50 border-b border-amber-100 px-5 py-2 text-xs text-amber-800 flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5" /> View only — this chat is owned by {active.assigned_name}. Reassign to type in it.
                </div>
              )}

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2.5 bg-slate-50">
                {messages.map((m) => {
                  const fromMe = m.sender_id === myUserId;
                  const fromPatient = m.sender_role === "patient";
                  return (
                    <div key={m.id} className={`flex ${fromPatient ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${fromPatient ? "bg-white border border-slate-200 text-slate-800 rounded-bl-md" : (fromMe ? "bg-purple-600 text-white rounded-br-md" : "bg-slate-200 text-slate-700 rounded-br-md")}`}>
                        {!fromPatient && !fromMe && <p className="text-[10px] font-semibold opacity-80 mb-0.5">{m.sender_name} ({m.sender_role})</p>}
                        {fromPatient && <p className="text-[10px] font-semibold text-purple-700 mb-0.5">{m.sender_name}</p>}
                        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{m.message}</p>
                        <p className={`text-[10px] mt-1 ${fromPatient ? "text-slate-400" : "text-white/70"}`}>{new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleSend} className="bg-white border-t border-slate-100 p-3 relative">
                {cannedOpen && cannedFiltered.length > 0 && (
                  <div className="absolute bottom-full left-3 right-3 mb-2 bg-white border border-slate-200 rounded-xl shadow-[0_10px_40px_-15px_rgba(0,0,0,0.25)] overflow-hidden z-10" data-testid="chat-canned-picker">
                    <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between">
                      <span>Canned responses · ↑↓ to navigate · Tab/Enter to insert · Esc to dismiss</span>
                      <span>{cannedFiltered.length} match{cannedFiltered.length === 1 ? "" : "es"}</span>
                    </div>
                    <ul className="max-h-72 overflow-y-auto">
                      {cannedFiltered.map((c, i) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            onMouseEnter={() => setCannedIdx(i)}
                            onClick={() => expandCanned(c)}
                            className={`w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors ${i === cannedIdx ? "bg-purple-50" : "hover:bg-slate-50"}`}
                            data-testid={`chat-canned-item-${c.slash}`}
                          >
                            <span className={`mt-0.5 inline-flex items-center text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${i === cannedIdx ? "bg-purple-600 text-white" : "bg-slate-100 text-slate-600"}`}>/{c.slash}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900">{c.title} <span className="text-[10px] font-normal text-slate-400 ml-1">[{c.category}]</span></p>
                              <p className="text-xs text-slate-500 truncate">{c.body}</p>
                            </div>
                            {(c.usage_count || 0) > 0 && (
                              <span className="text-[10px] text-slate-400 mt-1 shrink-0">used {c.usage_count}×</span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    placeholder={canTypeInActive ? "Type your reply… (try / for canned responses)" : "View only — claim or be reassigned to type"}
                    disabled={!canTypeInActive || sending}
                    className="rounded-full text-sm"
                    data-testid="chat-thread-input"
                  />
                  <Button type="submit" disabled={!canTypeInActive || sending || !input.trim()} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white w-10 h-10 p-0 flex-shrink-0" data-testid="chat-thread-send">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

// --- Sidebar primitives ---

function Section({ title, children, testId }) {
  return (
    <div className="border-b border-slate-100" data-testid={testId}>
      <p className="px-4 pt-4 pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">{title}</p>
      <ul>{children}</ul>
    </div>
  );
}

function Empty({ msg }) {
  return <li className="px-4 pb-4 text-xs text-slate-400 italic">{msg}</li>;
}

function ConvRow({ c, active, onClick, variant }) {
  const previewClass = variant === "queue" ? "border-l-2 border-amber-400" : variant === "watch" ? "border-l-2 border-slate-300" : "border-l-2 border-purple-500";
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-start gap-2 ${active ? "bg-purple-50" : ""} ${previewClass}`}
        data-testid={`chat-conv-${c.id}`}
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">{c.patient_name || "Patient"}</p>
          <p className="text-xs text-slate-500 truncate">{c.last_message || (variant === "queue" ? "Awaiting first reply…" : "—")}</p>
          {c.assigned_name && variant !== "queue" && (
            <p className="text-[10px] text-slate-400 mt-0.5 truncate">Agent: {c.assigned_name}</p>
          )}
        </div>
        {c.unread_staff > 0 && (
          <span className="bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1.5 mt-0.5">{c.unread_staff}</span>
        )}
        {variant === "queue" && (
          <span className="inline-flex items-center text-[10px] text-amber-600 font-semibold mt-0.5">
            <Hand className="w-3 h-3 mr-0.5" /> Claim
          </span>
        )}
      </button>
    </li>
  );
}

// Silence unused-var lints for assets reserved for future use.
void NEW_MSG_SOUND_URL;
void RefreshCw;
